import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Skeleton } from '@/components/ui/Skeleton'
import { useTenderCompanies } from '@/hooks/useCompanies'
import { cn, formatTHB } from '@/lib/utils'
import type { Company } from '@/lib/pocketbase'

interface CompaniesTabProps {
  tenderId: string
}

export function TenderCompaniesTab({ tenderId }: CompaniesTabProps) {
  const { data: companies, isLoading } = useTenderCompanies(tenderId)

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-4 p-4 border border-slate-200 rounded-lg">
              <Skeleton variant="circular" className="w-12 h-12" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-3 w-32" />
              </div>
            </div>
          ))}
        </div>
      </Card>
    )
  }

  if (!companies || companies.length === 0) {
    return (
      <Card className="p-8 text-center">
        <Icon
          icon="solar:buildings-2-line-duotone"
          className="w-12 h-12 text-slate-300 mx-auto mb-3"
        />
        <h3 className="font-semibold text-slate-900">ยังไม่มีบริษัท</h3>
        <p className="text-sm text-slate-500 mt-1">
          บริษัทที่เสนอราคาหรือชนะการประมูลจะแสดงที่นี่
        </p>
      </Card>
    )
  }

  // Separate winners and bidders
  const winners = companies.filter((c) => c.role === 'winner')
  const bidders = companies.filter((c) => c.role === 'bidder')

  return (
    <div className="space-y-6">
      {/* Winners */}
      {winners.length > 0 && (
        <Card padding="none">
          <div className="px-4 py-3 border-b border-slate-100 bg-emerald-50/50">
            <div className="flex items-center gap-2">
              <Icon icon="solar:medal-ribbon-line-duotone" className="w-5 h-5 text-emerald-600" />
              <h3 className="font-medium text-emerald-900">
                บริษัทที่ชนะการประมูล
              </h3>
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {winners.map((item) => (
              <CompanyItem
                key={item.company.id}
                company={item.company}
                role="winner"
                amount={item.award?.total_price}
                showDetails
              />
            ))}
          </div>
        </Card>
      )}

      {/* Bidders */}
      {bidders.length > 0 && (
        <Card padding="none">
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
            <div className="flex items-center gap-2">
              <Icon icon="solar:hand-money-line-duotone" className="w-5 h-5 text-slate-600" />
              <h3 className="font-medium text-slate-900">
                ผู้เสนอราคารายอื่น
              </h3>
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {bidders.map((item) => (
              <CompanyItem
                key={item.company.id}
                company={item.company}
                role="bidder"
                amount={item.bid?.bid_price}
                showDetails
              />
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatBox
          label="ผู้เสนอราคาทั้งหมด"
          value={companies.length}
          icon="solar:users-group-two-rounded-line-duotone"
        />
        <StatBox
          label="ผู้ชนะ"
          value={winners.length}
          icon="solar:medal-ribbon-line-duotone"
        />
        <StatBox
          label="อัตราส่วนการแข่งขัน"
          value={winners.length > 0 ? `${(companies.length / winners.length).toFixed(1)}:1` : '-'}
          icon="solar:chart-line-duotone"
        />
      </div>
    </div>
  )
}

function CompanyItem({
  company,
  role,
  amount,
  showDetails,
}: {
  company: Company
  role: 'winner' | 'bidder'
  amount?: number
  showDetails: boolean
}) {
  const initials = company.name
    ?.split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '?'

  return (
    <Link
      to={`/companies/${company.id}`}
      className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors"
    >
      {/* Avatar */}
      <div
        className={cn(
          'w-12 h-12 rounded-full flex items-center justify-center text-sm font-semibold',
          role === 'winner'
            ? 'bg-emerald-100 text-emerald-700'
            : 'bg-slate-100 text-slate-600'
        )}
      >
        {initials}
      </div>

      {/* Company info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-slate-900 truncate hover:text-blue-600">
            {company.name}
          </p>
          {role === 'winner' && (
            <Badge variant="success" size="sm">ผู้ชนะ</Badge>
          )}
        </div>
        <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
          {company.tax_id && (
            <span>เลขประจำตัวผู้เสียภาษี: {company.tax_id}</span>
          )}
          {showDetails && company.address && (
            <span className="truncate">{company.address}</span>
          )}
        </div>
      </div>

      {/* Bid/Award amount */}
      {amount && (
        <div className="text-right">
          <p className="text-xs text-slate-500">
            {role === 'winner' ? 'มูลค่าสัญญา' : 'ราคาที่เสนอ'}
          </p>
          <p className="font-medium text-slate-900">
            {formatTHB(amount)}
          </p>
        </div>
      )}

      {/* Arrow */}
      <Icon icon="solar:alt-arrow-right-line-duotone" className="w-5 h-5 text-slate-300" />
    </Link>
  )
}

function StatBox({
  label,
  value,
  icon,
}: {
  label: string
  value: string | number
  icon: string
}) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-slate-100 rounded-lg">
          <Icon icon={icon} className="w-5 h-5 text-slate-600" />
        </div>
        <div>
          <p className="text-sm text-slate-500">{label}</p>
          <p className="font-display text-xl font-semibold text-slate-900">{value}</p>
        </div>
      </div>
    </Card>
  )
}
